<template>
  <div class="responsive-date-range-picker">
    <!-- PC端使用 Element Plus -->
    <el-date-picker
      v-if="!isMobile"
      v-model="dateValue"
      type="daterange"
      :start-placeholder="startPlaceholder"
      :end-placeholder="endPlaceholder"
      :default-value="defaultValue"
      :disabled-date="disabledDate"
      @change="handleChange"
      :teleported="true"
      :style="style"
    />
    
    <!-- 移动端使用 Vant -->
    <van-config-provider :locale="jaLocale">
      <template v-if="isMobile">
        <van-field
          v-model="displayValue"
          :label="label"
          readonly
          :placeholder="placeholder"
          @click="showPicker = true"
          :style="style"
        />
        
        <!-- Vant 日期选择器弹窗 - 使用原生样式 -->
        <van-calendar
          title="期間を選択"
          v-model:show="showPicker"
          v-model="selectedDates"
          type="range"
          :min-date="minDate"
          :max-date="maxDate"
          :disabled-date="disabledDateVant"
          @confirm="handleVantConfirm"
          poppable
          position="bottom"
          :close-on-click-overlay="true"
          :formatter="dateFormatter"
          :show-title="true"
          :show-subtitle="true"
          switch-mode="year-month"
        />
      </template>
    </van-config-provider>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { ElDatePicker } from 'element-plus';
import { Field as VanField, Calendar as VanCalendar, ConfigProvider as VanConfigProvider } from 'vant';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';

// Vant 4.x 需要单独导入样式
import 'vant/es/field/style';
import 'vant/es/calendar/style';
import 'vant/es/config-provider/style';

// 设置 dayjs 为日文
dayjs.locale('ja');

// Vant 日文语言包配置
const jaLocale = {
  Calendar: {
    start: '開始',
    end: '終了',
    title: '日付を選択',
    confirm: '確定',
    startEnd: '開始/終了',
    weekdays: ['日', '月', '火', '水', '木', '金', '土'],
    monthTitle: (year, month) => `${year}年${month}月`,
    rangePrompt: (maxRange) => `最大${maxRange}日間を選択してください`,
  },
};

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  startPlaceholder: {
    type: String,
    default: ''
  },
  endPlaceholder: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: '期間を選択'
  },
  label: {
    type: String,
    default: ''
  },
  defaultValue: {
    type: Array,
    default: () => [new Date(), new Date()]
  },
  disabledDate: {
    type: Function,
    default: null
  },
  style: {
    type: String,
    default: 'width: 100%;'
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const { width } = useWindowSize();
const isMobile = computed(() => width.value < 768);

const dateValue = ref(props.modelValue);
const showPicker = ref(false);
const selectedDates = ref([]);

// Vant 日期选择器需要 Date 对象
const minDate = computed(() => {
  // 可以设置最小日期，这里设置为当前日期往前一年
  return new Date(new Date().getFullYear() - 1, 0, 1);
});

const maxDate = computed(() => {
  // 可以设置最大日期，这里设置为当前日期往后一年
  return new Date(new Date().getFullYear() + 1, 11, 31);
});

// 打开日历时传入的初始区间，保证区间选择与回显正确
const defaultDateForCalendar = computed(() => {
  const val = props.modelValue;
  if (val && Array.isArray(val) && val.length >= 2 && val[0] && val[1]) {
    return [new Date(val[0]), new Date(val[1])];
  }
  return undefined;
});

// 显示值（移动端）- 根据屏幕宽度调整日期格式
const displayValue = computed(() => {
  if (!props.modelValue || props.modelValue.length !== 2) {
    return '';
  }
  const [start, end] = props.modelValue;
  if (!start || !end) {
    return '';
  }
  
  // 移动端使用更短的日期格式以节省空间
  if (isMobile.value) {
    // 小屏幕使用短格式：MM/DD - MM/DD
    if (width.value < 480) {
      return `${dayjs(start).format('MM/DD')} - ${dayjs(end).format('MM/DD')}`;
    }
    // 中等屏幕使用：YYYY/MM/DD - YYYY/MM/DD
    return `${dayjs(start).format('YYYY/MM/DD')} - ${dayjs(end).format('YYYY/MM/DD')}`;
  }
  
  // PC端使用完整格式
  return `${dayjs(start).format('YYYY-MM-DD')} - ${dayjs(end).format('YYYY-MM-DD')}`;
});

// 监听 modelValue 变化，同步到内部状态
watch(() => props.modelValue, (newVal) => {
  dateValue.value = newVal;
  // 同步到 Vant Calendar
  if (newVal && Array.isArray(newVal) && newVal.length === 2) {
    selectedDates.value = newVal;
  }
}, { immediate: true, deep: true });

// 监听 dateValue 变化（PC端）
watch(dateValue, (newVal) => {
  if (!isMobile.value) {
    emit('update:modelValue', newVal);
    emit('change', newVal);
  }
});

// PC端变化处理
const handleChange = (value) => {
  dateValue.value = value;
  emit('update:modelValue', value);
  emit('change', value);
};

// Vant 日期选择器确认
const handleVantConfirm = (values) => {
  // Vant Calendar 的 confirm 事件返回的是选中的日期数组
  if (values && Array.isArray(values)) {
    let dateRange;
    if (values.length === 2) {
      // 范围选择：两个日期
      dateRange = [values[0], values[1]];
    } else if (values.length === 1) {
      // 单个日期，使用同一天作为范围
      dateRange = [values[0], values[0]];
    } else {
      return;
    }
    
    dateValue.value = dateRange;
    selectedDates.value = dateRange;
    emit('update:modelValue', dateRange);
    emit('change', dateRange);
    showPicker.value = false;
  }
};

// Vant 禁用日期处理
const disabledDateVant = (date) => {
  if (props.disabledDate) {
    // 将 Vant 的 Date 对象转换为 Element Plus 格式
    return props.disabledDate(date);
  }
  return false;
};

// 日期格式化函数 - 用于显示日文月份和星期
const dateFormatter = (day) => {
  if (!day || !day.date) return day;
  
  const date = day.date;
  const dayOfWeek = date.getDay();
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  const weekday = weekdays[dayOfWeek];
  
  return {
    ...day,
    bottomInfo: weekday,
  };
};

// 监听弹窗显示，隐藏"日期选择"按钮
let observer = null;
watch(showPicker, (isShow) => {
  if (isShow && isMobile.value) {
    // 先清理之前的 observer
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    
    // 延迟执行，确保 DOM 已渲染
    setTimeout(() => {
      const hideDateSelectButton = () => {
        // 查找所有可能的日历弹窗
        const popupEl = document.querySelector('.van-popup');
        if (!popupEl) return;
        
        const calendarEl = popupEl.querySelector('.van-calendar');
        if (!calendarEl) return;
        
        const headerEl = calendarEl.querySelector('.van-calendar__header');
        if (!headerEl) return;
        
        // 方法1: 隐藏所有非标题、非副标题、非关闭按钮的元素
        const allChildren = Array.from(headerEl.children);
        allChildren.forEach((child) => {
          const isTitle = child.classList.contains('van-calendar__header-title');
          const isSubtitle = child.classList.contains('van-calendar__header-subtitle');
          const isClose = child.classList.contains('van-calendar__close') || 
                         child.querySelector('.van-calendar__close');
          
          if (!isTitle && !isSubtitle && !isClose) {
            // 检查是否包含"日期选择"文本
            const text = child.textContent || '';
            if (text.includes('日期选择') || text.includes('日期') || text.includes('选择')) {
              child.style.display = 'none';
              child.style.visibility = 'hidden';
              child.style.opacity = '0';
              child.style.width = '0';
              child.style.height = '0';
              child.style.padding = '0';
              child.style.margin = '0';
            }
          }
        });
        
        // 方法2: 查找所有按钮（除了关闭按钮）
        const buttons = headerEl.querySelectorAll('button');
        buttons.forEach((btn) => {
          const btnText = btn.textContent || '';
          const isClose = btn.classList.contains('van-calendar__close') || 
                         btn.closest('.van-calendar__close');
          
          if (!isClose && (btnText.includes('日期') || btnText.includes('选择'))) {
            btn.style.display = 'none';
            btn.style.visibility = 'hidden';
            btn.style.opacity = '0';
            btn.style.width = '0';
            btn.style.height = '0';
            btn.style.padding = '0';
            btn.style.margin = '0';
          }
        });
        
        // 方法3: 查找所有包含"日期选择"文本的元素
        const walker = document.createTreeWalker(
          headerEl,
          NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
          null
        );
        
        let node;
        while ((node = walker.nextNode())) {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent || '';
            if (text.includes('日期选择') || text.includes('日期')) {
              const parent = node.parentElement;
              if (parent && 
                  !parent.closest('.van-calendar__header-title') && 
                  !parent.closest('.van-calendar__header-subtitle')) {
                parent.style.display = 'none';
                parent.style.visibility = 'hidden';
                parent.style.opacity = '0';
              }
            }
          }
        }
      };
      
      // 立即执行一次
      hideDateSelectButton();
      
      // 使用 MutationObserver 持续监听 DOM 变化
      observer = new MutationObserver(() => {
        hideDateSelectButton();
      });
      
      const popupEl = document.querySelector('.van-popup');
      if (popupEl) {
        observer.observe(popupEl, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true
        });
      }
    }, 100);
  } else {
    // 弹窗关闭时清理 observer
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }
});
</script>

<style lang="scss" scoped>
.responsive-date-range-picker {
  width: 100%;
  
  // 移动端样式调整
  :deep(.van-field) {
    padding: 0;
    background: var(--bg-panel);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    min-height: 32px;
    width: 100%;
    overflow: hidden;
    transition: border-color 0.2s;
    
    &:active {
      border-color: var(--brand-color);
    }
    
    .van-cell {
      padding: 0;
      min-height: 32px;
      display: flex;
      align-items: center;
      width: 100%;
      background: transparent;
    }
    
    .van-field__label {
      display: none;
    }
    
    .van-field__body {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      height: 32px;
      position: relative;
    }
    
    .van-field__control {
      font-size: 14px;
      padding: 0 12px;
      line-height: 40px;
      height: 32px;
      width: 100%;
      overflow: visible;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
      flex: 1;
      color: var(--text-primary);
      background: transparent;
    }
    
    // 隐藏箭头图标
    .van-field__right-icon {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    
    // 不同分辨率适配
    @media (max-width: 480px) {
      min-height: 32px;
      
      .van-cell {
        min-height: 32px;
      }
      
      .van-field__body {
        display: flex !important;
        align-items: center !important;
        height: 32px;
      }
      
      .van-field__control {
        font-size: 13px;
        padding: 0 10px;
        line-height: 32px;
        height: 32px;
        max-width: 140px; // MM/DD - MM/DD 格式不需要太宽
      }
      
      .van-field__right-icon {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        width: 0 !important;
        height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }
    }
    
    @media (max-width: 375px) {
      min-height: 32px;
      
      .van-cell {
        min-height: 32px;
      }
      
      .van-field__body {
        height: 32px;
      }
      
      .van-field__control {
        font-size: 12px;
        padding: 0 8px;
        line-height: 32px;
        height: 32px;
        max-width: 130px; // MM/DD - MM/DD 格式不需要太宽
      }
      
      .van-field__body {
        display: flex !important;
        align-items: center !important;
        height: 32px;
      }
      
      .van-field__right-icon {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        width: 0 !important;
        height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }
    }
    
    @media (max-width: 320px) {
      min-height: 32px;
      
      .van-cell {
        min-height: 32px;
      }
      
      .van-field__body {
        height: 32px;
      }
      
      .van-field__control {
        font-size: 11px;
        padding: 0 6px;
        line-height: 32px;
        height: 32px;
        max-width: 120px; // MM/DD - MM/DD 格式不需要太宽
      }
      
      .van-field__body {
        display: flex !important;
        align-items: center !important;
        height: 32px;
      }
      
      .van-field__right-icon {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        width: 0 !important;
        height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }
    }
  }
  
  // Vant Calendar 弹窗样式
  :deep(.van-popup) {
    z-index: 1001 !important;
  }
  
  // 遮罩层样式
  :deep(.van-overlay) {
    z-index: 1000 !important;
    background-color: rgba(0, 0, 0, 0.7) !important;
  }
  
  // Vant Calendar 日文样式覆盖
  :deep(.van-calendar) {
    // 月份标题
    .van-calendar__month-title {
      font-weight: 600;
    }
  }
  
  // 通过 CSS 覆盖星期标题为日文
  :deep(.van-calendar__weekday) {
    font-size: 0 !important;
    
    &:nth-child(1)::after { 
      content: '日';
      font-size: 13px;
      display: inline-block;
    }
    &:nth-child(2)::after { 
      content: '月';
      font-size: 13px;
      display: inline-block;
    }
    &:nth-child(3)::after { 
      content: '火';
      font-size: 13px;
      display: inline-block;
    }
    &:nth-child(4)::after { 
      content: '水';
      font-size: 13px;
      display: inline-block;
    }
    &:nth-child(5)::after { 
      content: '木';
      font-size: 13px;
      display: inline-block;
    }
    &:nth-child(6)::after { 
      content: '金';
      font-size: 13px;
      display: inline-block;
    }
    &:nth-child(7)::after { 
      content: '土';
      font-size: 13px;
      display: inline-block;
    }
  }
  
  // 覆盖标题为日文
  :deep(.van-calendar__header-title) {
    position: relative;
    
    // 隐藏原始文本
    > * {
      opacity: 0;
      font-size: 0;
    }
    
    // 显示日文标题
    // &::after {
    //   content: '日付を選択';
    //   position: absolute;
    //   left: 0;
    //   top: 0;
    //   font-size: 18px;
    //   font-weight: 600;
    //   color: #323233;
    //   opacity: 1;
    // }
  }
  
  // 仅隐藏标题栏的“日期选择”等无关按钮，保留副标题区域的 « < > » 导航按钮
  :deep(.van-calendar__header) {
    > button:not(.van-calendar__close) {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }
  }
  
  // 导航按钮 « < > » 使用黄色主题
  :deep(.van-calendar__header-action) {
    color: var(--van-text-color) !important;
    .van-icon {
      color: inherit;
    }
    &:not([class*="disabled"]):active {
      color: var(--brand-color) !important;
    }
  }
  
  // 仅隐藏副标题中间的文字节点，保留 « < > » 导航按钮可见
  :deep(.van-calendar__header-subtitle-text) {
    font-size: 14px;
    color: var(--text-secondary);
  }
  
  // 覆盖确认按钮文字和颜色
  :deep(.van-calendar__confirm) {
    position: relative;
    background-color: var(--brand-color) !important;
    border-color: var(--brand-color) !important;
    color: #181818 !important;
    
    &:active {
      background-color: var(--brand-dark) !important;
      border-color: var(--brand-dark) !important;
    }
    
    > * {
      opacity: 0;
      font-size: 0;
    }
    
    &::after {
      content: '確定';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      font-size: 16px;
      color: #181818 !important;
      opacity: 1;
      font-weight: 600;
    }
  }
  
  // 修改选中日期的背景色为黄色主题
  :deep(.van-calendar__selected-day) {
    background-color: var(--brand-color) !important;
    color: #181818 !important;
  }
  
  // 修改日期范围选中区域的背景色
  :deep(.van-calendar__day--start),
  :deep(.van-calendar__day--end),
  :deep(.van-calendar__day--middle),
  :deep(.van-calendar__day--multiple-selected) {
    background-color: var(--brand-color) !important;
    color: #181818 !important;
  }
  
  // 修改日期范围中间区域的背景色（浅黄色）
  :deep(.van-calendar__day--middle) {
    background-color: color-mix(in srgb, var(--brand-color) 30%, transparent) !important;
  }
  
  // 修改日期悬停效果
  :deep(.van-calendar__day:active) {
    background-color: var(--brand-dark) !important;
    color: #181818 !important;
  }
  
  // 修改今天日期的标记颜色
  :deep(.van-calendar__day--today) {
    color: var(--brand-color) !important;
  }
}
</style>

<!-- teleported 到 body 的日历弹层：补充暗色主题（不受 scoped 父级限制） -->
<style lang="scss">
html.dark {
  .van-popup:has(.van-calendar),
  .van-calendar {
    background: var(--bg-panel) !important;
    color: var(--text-primary) !important;
  }

  .van-calendar__header,
  .van-calendar__header-title,
  .van-calendar__header-subtitle,
  .van-calendar__month-title,
  .van-calendar__weekday,
  .van-calendar__day {
    color: var(--text-primary) !important;
    background: transparent;
  }

  .van-calendar__header {
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
  }

  .van-calendar__weekday {
    color: var(--text-secondary) !important;
  }

  .van-calendar__bottom-info,
  .van-calendar__top-info {
    color: var(--text-secondary) !important;
  }

  .van-calendar__confirm {
    background-color: var(--brand-color) !important;
    border-color: var(--brand-color) !important;
    color: #181818 !important;
  }

  .van-calendar__selected-day,
  .van-calendar__day--start,
  .van-calendar__day--end,
  .van-calendar__day--multiple-selected {
    background-color: var(--brand-color) !important;
    color: #181818 !important;
  }

  .van-calendar__day--middle {
    background-color: color-mix(in srgb, var(--brand-color) 30%, transparent) !important;
    color: var(--text-primary) !important;
  }

  .van-calendar__day--today {
    color: var(--brand-color) !important;
  }

  .van-calendar__header-action {
    color: var(--text-primary) !important;
  }
}
</style>

